// @flow
import React, { useEffect, useRef } from 'react';
import type { Node } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { FormattedMessage } from 'react-intl';
import {
  formatDateDB,
  formatDateDisplayUTC,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import { BadgeStatus } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import {
  Banner,
  Box,
  Spinner,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';

import useDebouncedValue from '../../hooks/useDebouncedValue';
import useIntl from '../../hooks/useIntl';
import { getCertificationTypePlainName } from '../../containers/GeneralAffordable/constants';
import { impactMessages as messages } from './messages';

// The pieces the Move-In and Move-Out Date corrections (household-mi-mo-date-changes) share: the
// debounced dry-run preview, its status, and the affected-record list.

export type PreviewStatus = 'idle' | 'loading' | 'invalid' | 'valid' | 'error';

/** What the date field can hold: the picker's moment, a Date, or typed text. */
export type DateFieldValue = ?(string | Date | moment$Moment);

export type CertAction =
  | 'ALIGN_EFFECTIVE_DATE'
  | 'START_HUD_CORRECTION'
  | 'REOPEN_HUD_CERTIFICATION'
  | 'FLAG_NEEDS_REVIEW';

export type PreviewCert = {
  id: string,
  programName: string,
  certificationType: string,
  /** The document to review and regenerate, e.g. "HUD-50059", "Form RD 3560-8". */
  formName: string,
  effectiveDate: ?string,
  action: CertAction,
  newEffectiveDate?: ?string,
};

export type PreviewWarning = {
  code: string,
  message: string,
  certificationId?: string,
};

/** The record lists of either preview; each entry is one row of the impact list. */
type ImpactRecords = {
  certifications?: ?Array<mixed>,
  activeLease?: ?mixed,
  stayLeases?: ?Array<mixed>,
  affectedLeases?: ?Array<mixed>,
  transfer?: ?mixed,
};

/**
 * YYYY-MM-DD for the field's value, or null when it isn't a date. The picker's value is the day the
 * user chose, so it is formatted as-is (`utc = false`): converting to UTC would move a local
 * midnight east of UTC to the previous day, and formatDateDB's UTC path also shifts the moment the
 * form holds.
 */
export const toPreviewDate = (value: DateFieldValue): ?string =>
  formatDateDB(value, false) || null;

const PREVIEW_DEBOUNCE_MS = 300;
const DISPLAY_FORMAT = 'MM/DD/YYYY';
export const NO_DATE = '---';

// Calendar days: formatted in UTC so the day never shifts with the browser's timezone.
export const toDisplay = (isoDate: ?string): string =>
  formatDateDisplayUTC(isoDate, DISPLAY_FORMAT);

const ROW_SX = {
  px: 1.5,
  py: 1,
  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
};

const CHIP_SX = {
  primary: { color: 'primary.main', backgroundColor: 'primary.lighter' },
  warning: { color: 'warning.main', backgroundColor: 'warning.lighter' },
  success: { color: 'success.main', backgroundColor: 'success.lighter' },
};

export const ImpactRow = ({
  title,
  description,
  detail,
  chip,
  chipColor,
  action,
  dataTest,
}: {
  title: string,
  description: string,
  /** A second line under the description, e.g. the form the row concerns. */
  detail?: string,
  chip: string,
  chipColor: 'primary' | 'warning' | 'success',
  /** A control under the text, full width, e.g. the Active Lease row's Upload button. */
  action?: Node,
  dataTest: string,
}): Node => (
  <Box sx={ROW_SX} data-test={dataTest}>
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="flex-start"
      spacing={1}
    >
      <Box>
        <Typography variant="labelMedium" component="div">
          {title}
        </Typography>
        <Typography variant="bodySmall" color="text.secondary" component="div">
          {description}
        </Typography>
        {detail && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            component="div"
          >
            {detail}
          </Typography>
        )}
      </Box>
      <BadgeStatus
        sx={{ flexShrink: 0, whiteSpace: 'nowrap', ...CHIP_SX[chipColor] }}
      >
        {chip}
      </BadgeStatus>
    </Stack>
    {action && <Box mt={1}>{action}</Box>}
  </Box>
);

/** The records a correction changes: one row each. */
export const countImpactRecords = (impacts: ?ImpactRecords): number =>
  (impacts?.certifications?.length ?? 0) +
  (impacts?.activeLease ? 1 : 0) +
  (impacts?.stayLeases?.length ?? 0) +
  (impacts?.affectedLeases?.length ?? 0) +
  (impacts?.transfer ? 1 : 0);

/**
 * One certification of a preview. Program-agnostic: names come from the cert's own program (HUD,
 * RD, PH, LIHTC, state programs…), and the current effective date tells apart same-program,
 * same-type certs of different years. Every row names its form: the Needs Review text says it
 * inline (Joe's wording), the other rows show it on a second line.
 */
export const CertificationImpactRow = ({
  cert,
  dataTest,
  needsReviewDescription,
}: {
  cert: PreviewCert,
  dataTest: string,
  /** The Needs Review row's text, which names why the cert is flagged. */
  needsReviewDescription: string,
}): Node => {
  const intl = useIntl();
  const typeName =
    getCertificationTypePlainName(cert.programName, cert.certificationType) ??
    cert.certificationType;
  const titleValues = { programName: cert.programName, typeName };
  const title = cert.effectiveDate
    ? intl.formatMessage(messages.certificationTitleWithDate, {
        ...titleValues,
        date: toDisplay(cert.effectiveDate),
      })
    : intl.formatMessage(messages.certificationTitle, titleValues);
  const formDetail = intl.formatMessage(messages.formDetail, {
    formName: cert.formName,
  });

  if (cert.action === 'ALIGN_EFFECTIVE_DATE') {
    return (
      <ImpactRow
        dataTest={dataTest}
        title={title}
        description={intl.formatMessage(messages.alignDescription, {
          date: toDisplay(cert.newEffectiveDate),
        })}
        detail={formDetail}
        chip={intl.formatMessage(messages.autoUpdated)}
        chipColor="primary"
      />
    );
  }
  if (cert.action === 'START_HUD_CORRECTION') {
    return (
      <ImpactRow
        dataTest={dataTest}
        title={title}
        description={intl.formatMessage(messages.hudCorrectionDescription)}
        detail={formDetail}
        chip={intl.formatMessage(messages.hudCorrectionChip)}
        chipColor="warning"
      />
    );
  }
  if (cert.action === 'REOPEN_HUD_CERTIFICATION') {
    return (
      <ImpactRow
        dataTest={dataTest}
        title={title}
        description={intl.formatMessage(messages.hudReopenDescription, {
          date: toDisplay(cert.newEffectiveDate),
        })}
        detail={formDetail}
        chip={intl.formatMessage(messages.hudReopenChip)}
        chipColor="warning"
      />
    );
  }
  return (
    <ImpactRow
      dataTest={dataTest}
      title={title}
      description={needsReviewDescription}
      chip={intl.formatMessage(messages.needsReview)}
      chipColor="warning"
    />
  );
};

/**
 * The frame of a valid preview's affected-record list: the review banner, the record-count header
 * (which names the modal's scrolling impact region through `countId`), the rows, then the warnings.
 */
export const ImpactList = ({
  testIdPrefix,
  countId,
  reviewBanner,
  recordCount,
  warnings,
  children,
}: {
  /** 'mi' or 'mo': the data-test prefix of the list's parts. */
  testIdPrefix: string,
  countId: string,
  reviewBanner: string,
  recordCount: number,
  warnings: ?Array<PreviewWarning>,
  children: Node,
}): Node => (
  <Box mt={2} data-test={`${testIdPrefix}-impacts`}>
    <Banner
      status="warning"
      sx={{ mb: 1.5 }}
      message={
        <Typography
          variant="bodySmall"
          component="span"
          sx={{ color: 'warning.main' }}
        >
          {reviewBanner}
        </Typography>
      }
    />
    <Box border="1px solid" borderColor="divider" borderRadius={1}>
      <Box px={1.5} py={1} sx={{ backgroundColor: 'action.hover' }}>
        <Typography
          variant="labelMedium"
          id={countId}
          data-test={`${testIdPrefix}-impacts-count`}
        >
          <FormattedMessage
            {...messages.recordsAffected}
            values={{ count: recordCount }}
          />
        </Typography>
      </Box>
      {children}
    </Box>
    {warnings?.map((warning) => (
      <Typography
        key={`${warning.code}-${warning.certificationId ?? ''}`}
        variant="bodySmall"
        color="warning.main"
        component="div"
        mt={1}
        data-test={`${testIdPrefix}-impact-warning`}
      >
        {warning.message}
      </Typography>
    ))}
  </Box>
);

export type StatusChangeHandler = (
  status: PreviewStatus,
  date: ?string,
  recordCount: number,
) => void;

/**
 * The dry run of a date correction for the date in the field. Once the field passes the form's
 * rules and differs from the current date, it asks the backend (debounced) what a Save would do,
 * and reports the status with the YYYY-MM-DD date it answers (null when there is none) and the
 * number of records listed (0 unless valid), so the modal never enables Save for a newer field
 * value whose status hasn't arrived yet.
 *
 * The query is keyed on `queryKey` (everything the request is scoped by) plus the date: a reply for
 * an older date, or for another property or stay, never answers the current one, and react-query
 * aborts the superseded request through `signal`.
 *
 * A date's preview is cached for the modal session (never stale, kept without observers): the
 * Move-In modal hides while its Upload Corrected Lease dialog is open, which unmounts this list,
 * and showing it again must not refetch (a refetch reads as `loading` and turns Save off). Any
 * other unmount (the modal closed or saved) drops every preview of the kind (`queryKey[0]`), not
 * only the scope's: the parent may forget the stay in the same update that closes the modal, so
 * the list's last scope is not always the one whose previews were cached. They are dropped once
 * the list is gone, not when the modal starts closing: a re-render during the closing fade would
 * rebuild (and refetch) a removed query.
 */
export const useCorrectionPreview = <T: ImpactRecords>({
  queryKey,
  proposedDate,
  currentDate,
  clientValid,
  fetchPreview,
  onStatusChange,
  keepCacheOnUnmount = false,
}: {
  queryKey: Array<mixed>,
  proposedDate: DateFieldValue,
  /** YYYY-MM-DD; an unchanged date has nothing to preview. */
  currentDate: ?string,
  /** The form's own validation passed. */
  clientValid: boolean,
  fetchPreview: (date: string, signal: AbortSignal) => Promise<Object>,
  onStatusChange: StatusChangeHandler,
  /** The list is only hidden for a while (the upload dialog): keep the previews for its return. */
  keepCacheOnUnmount?: boolean,
}): ({
  status: PreviewStatus,
  impacts: ?T,
  /** The server's rule message when the date is rejected. */
  message: ?string,
  retry: () => void,
}) => {
  const isoDate = clientValid ? toPreviewDate(proposedDate) : null;
  const candidate = isoDate && isoDate !== currentDate ? isoDate : null;
  const debounced = useDebouncedValue(candidate, PREVIEW_DEBOUNCE_MS);
  const isSettled = debounced === candidate;

  const query = useQuery(
    [...queryKey, debounced],
    ({ signal }) => fetchPreview(debounced ?? '', signal),
    {
      enabled: Boolean(debounced) && isSettled,
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const queryClient = useQueryClient();
  const keepCacheRef = useRef(keepCacheOnUnmount);
  keepCacheRef.current = keepCacheOnUnmount;
  const queryKindRef = useRef(queryKey[0]);
  queryKindRef.current = queryKey[0];
  useEffect(
    () => () => {
      if (!keepCacheRef.current) {
        queryClient.removeQueries([queryKindRef.current]);
      }
    },
    [queryClient],
  );

  let status: PreviewStatus = 'idle';
  if (candidate) {
    if (!isSettled || query.isLoading || query.isFetching) {
      status = 'loading';
    } else if (query.isError) {
      status = 'error';
    } else if (query.data?.valid === false) {
      status = 'invalid';
    } else if (query.data?.valid) {
      status = 'valid';
    } else {
      status = 'loading';
    }
  }
  const impacts: ?T = status === 'valid' ? query.data.impacts : null;
  const recordCount = countImpactRecords(impacts);

  useEffect(() => {
    onStatusChange(status, candidate, recordCount);
  }, [status, candidate, recordCount]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    status,
    impacts,
    message: status === 'invalid' ? query.data.message : null,
    retry: () => {
      query.refetch();
    },
  };
};

/**
 * Drops the cached previews of `queryKeyPrefix` (e.g. 'moveInDateCorrectionPreview') when the
 * modal unmounts, e.g. the page is left while the upload dialog holds them. Renders nothing; the
 * modal is a class component and can't call the hook itself.
 */
export const CorrectionPreviewCacheReset = ({
  queryKeyPrefix,
}: {
  queryKeyPrefix: string,
}): Node => {
  const queryClient = useQueryClient();
  useEffect(
    () => () => queryClient.removeQueries([queryKeyPrefix]),
    [queryClient, queryKeyPrefix],
  );
  return null;
};

/**
 * What the preview area shows while there is no list: nothing (idle), a spinner (loading), the
 * server's rule message under the field (invalid), or a load error with Try again (error).
 */
export const PreviewStatusView = ({
  testIdPrefix,
  status,
  message,
  onRetry,
}: {
  testIdPrefix: string,
  status: PreviewStatus,
  message: ?string,
  onRetry: () => void,
}): Node => {
  if (status === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        py={2}
        data-test={`${testIdPrefix}-impacts-loading`}
      >
        <Spinner small />
      </Box>
    );
  }
  if (status === 'invalid') {
    return (
      <div className="has-error" data-test={`${testIdPrefix}-impacts-invalid`}>
        <span className="help-block">{message}</span>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="has-error" data-test={`${testIdPrefix}-impacts-error`}>
        <span className="help-block">
          <FormattedMessage {...messages.loadError} />{' '}
          <button
            type="button"
            className="btn btn-link"
            style={{ padding: 0, verticalAlign: 'baseline' }}
            onClick={onRetry}
          >
            <FormattedMessage {...messages.tryAgain} />
          </button>
        </span>
      </div>
    );
  }
  return null;
};
